(function () {
  'use strict';

  if (customElements.get('bombae-card-product-v2-form')) return;

  const DEBOUNCE_MS = 350;
  const subscribers = new Set();
  const cartState = {
    loaded: false,
    cart: null,
    byVariantId: new Map(),
    pending: new Map(),
    cartPromise: null,
  };

  function emitChange() {
    subscribers.forEach((callback) => callback());
  }

  function subscribe(callback) {
    subscribers.add(callback);
    return function unsubscribe() {
      subscribers.delete(callback);
    };
  }

  function getPendingState(variantId) {
    const key = String(variantId);

    if (!cartState.pending.has(key)) {
      cartState.pending.set(key, {
        desiredQty: null,
        timerId: null,
        syncing: false,
      });
    }

    return cartState.pending.get(key);
  }

  function buildVariantMap(cart) {
    const nextMap = new Map();

    (cart?.items || []).forEach((item) => {
      const variantId = String(item.variant_id);
      const existing = nextMap.get(variantId);

      if (existing) {
        existing.quantity += item.quantity;
        existing.item = existing.item || item;
        existing.lineKey = existing.lineKey || item.key;
      } else {
        nextMap.set(variantId, {
          quantity: item.quantity,
          lineKey: item.key,
          item,
        });
      }
    });

    cartState.cart = cart;
    cartState.byVariantId = nextMap;
    cartState.loaded = true;
  }

  function getCartUrl() {
    return `${window.routes.cart_url}.js`;
  }

  async function fetchCart() {
    if (cartState.cartPromise) return cartState.cartPromise;

    cartState.cartPromise = fetch(getCartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to load cart');
        }

        return response.json();
      })
      .then((cart) => {
        buildVariantMap(cart);
        emitChange();
        return cart;
      })
      .finally(() => {
        cartState.cartPromise = null;
      });

    return cartState.cartPromise;
  }

  function getServerQuantity(variantId) {
    return cartState.byVariantId.get(String(variantId))?.quantity || 0;
  }

  function getLineKey(variantId) {
    return cartState.byVariantId.get(String(variantId))?.lineKey || null;
  }

  function getRequestedSections() {
    const sections = ['cart-icon-bubble'];

    if (document.querySelector('cart-drawer')) {
      sections.unshift('cart-drawer');
    }

    return sections;
  }

  function updateCartIconBubble(html) {
    if (!html) return;

    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('.shopify-section');
    bubble.innerHTML = source ? source.innerHTML : parsed.body.innerHTML;
  }

  function applySectionResponse(response) {
    if (!response?.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && response.sections['cart-drawer']) {
      cartDrawer.renderContents({ sections: response.sections });
      return;
    }

    updateCartIconBubble(response.sections['cart-icon-bubble']);
  }

  async function requestAdd(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    formData.append('sections', getRequestedSections());
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig();
    config.headers.Accept = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.routes.cart_add_url, config);
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to add item to cart');
    }

    applySectionResponse(data);
    await fetchCart();

    return cartState.cart;
  }

  async function requestChange(variantId, quantity) {
    let lineKey = getLineKey(variantId);

    if (!lineKey) {
      await fetchCart();
      lineKey = getLineKey(variantId);
    }

    if (!lineKey && quantity > 0) {
      return requestAdd(variantId, quantity);
    }

    if (!lineKey) {
      buildVariantMap(cartState.cart || { items: [] });
      emitChange();
      return cartState.cart;
    }

    const payload = {
      id: lineKey,
      quantity,
      sections: getRequestedSections(),
      sections_url: window.location.pathname,
    };

    const config = fetchConfig();
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(payload);

    const response = await fetch(window.routes.cart_change_url, config);
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to update cart');
    }

    applySectionResponse(data);
    buildVariantMap(data);
    emitChange();

    return data;
  }

  async function syncVariant(variantId, desiredQty) {
    const currentQty = getServerQuantity(variantId);

    if (desiredQty === currentQty) {
      return cartState.cart;
    }

    if (desiredQty <= 0) {
      if (currentQty <= 0) return cartState.cart;
      return requestChange(variantId, 0);
    }

    if (currentQty <= 0) {
      return requestAdd(variantId, desiredQty);
    }

    return requestChange(variantId, desiredQty);
  }

  function publishCartUpdate(source, cartData, variantId) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source,
      cartData,
      variantId,
    });
  }

  function scheduleSync(variantId, delay) {
    const pending = getPendingState(variantId);

    if (pending.timerId) {
      window.clearTimeout(pending.timerId);
      pending.timerId = null;
    }

    if (pending.syncing) {
      emitChange();
      return;
    }

    pending.timerId = window.setTimeout(function () {
      flushVariant(variantId);
    }, delay);
  }

  async function flushVariant(variantId) {
    const pending = getPendingState(variantId);

    if (pending.syncing) return;

    const serverQty = getServerQuantity(variantId);
    const desiredQty = pending.desiredQty;

    pending.timerId = null;

    if (desiredQty == null || desiredQty === serverQty) {
      pending.desiredQty = null;
      emitChange();
      return;
    }

    pending.syncing = true;
    emitChange();

    try {
      const cart = await syncVariant(variantId, desiredQty);
      publishCartUpdate('bombae-card-product-v2', cart, variantId);
      /* /cart/add path is toasted by the global interceptor;
         we only need to cover the /cart/change increment path. */
      if (desiredQty > serverQty && serverQty > 0 && typeof window.showBombaeToast === 'function') {
        window.showBombaeToast('Product added to cart');
      }
    } catch (error) {
      console.error(error);
      if (typeof window.showBombaeToast === 'function') {
        const isAdd = desiredQty > serverQty;
        const isRemove = desiredQty <= 0;
        const msg = isRemove ? 'Could not remove. Try again.'
                  : isAdd ? 'Could not add to cart. Try again.'
                  : 'Could not update cart. Try again.';
        window.showBombaeToast(msg);
      }
      await fetchCart().catch(() => {});
      pending.desiredQty = null;
    } finally {
      pending.syncing = false;

      const latestServerQty = getServerQuantity(variantId);
      if (pending.desiredQty === latestServerQty) {
        pending.desiredQty = null;
      } else if (pending.desiredQty != null) {
        scheduleSync(variantId, 0);
      }

      emitChange();
    }
  }

  function setDesiredQuantity(variantId, quantity, immediate) {
    const pending = getPendingState(variantId);
    pending.desiredQty = Math.max(0, quantity);
    if (immediate) {
      if (pending.timerId) {
        window.clearTimeout(pending.timerId);
        pending.timerId = null;
      }
      if (!pending.syncing) {
        flushVariant(variantId);
      } else {
        emitChange();
      }
    } else {
      scheduleSync(variantId, DEBOUNCE_MS);
      emitChange();
    }
  }

  function getVariantViewState(variantId) {
    const pending = getPendingState(variantId);
    const serverQty = getServerQuantity(variantId);
    const quantity = pending.desiredQty == null ? serverQty : pending.desiredQty;

    return {
      quantity,
      pending: pending.desiredQty != null && pending.desiredQty !== serverQty,
      syncing: pending.syncing,
      loaded: cartState.loaded,
    };
  }

  class BombaeCardProductV2Form extends HTMLElement {
    constructor() {
      super();

      this.onStoreChange = this.render.bind(this);
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.addButton = this.querySelector('[data-action="add"]');
      this.minusButton = this.querySelector('[data-action="decrease"]');
      this.plusButton = this.querySelector('[data-action="increase"]');
      this.quantitySelector = this.querySelector('[data-role="quantity-selector"]');
      this.quantityValue = this.querySelector('[data-role="quantity-value"]');

      this.unsubscribe = subscribe(this.onStoreChange);

      if (!this.dataset.bound) {
        this.dataset.bound = 'true';
        this.addEventListener('click', this.handleClick.bind(this));
      }

      this.render();
      fetchCart().catch((error) => console.error(error));
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async handleClick(event) {
      const actionButton = event.target.closest('[data-action]');
      if (!actionButton || !this.variantId) return;

      event.preventDefault();

      const action = actionButton.dataset.action;

      if (action === 'add') {
        this._clickPending = true;
        this.render();
      }

      if (!cartState.loaded) {
        try {
          await fetchCart();
        } catch (error) {
          console.error(error);
          this._clickPending = false;
          this.render();
          return;
        }
      }

      const viewState = getVariantViewState(this.variantId);
      let nextQuantity = viewState.quantity;

      if (action === 'add') {
        nextQuantity = viewState.quantity > 0 ? viewState.quantity : 1;
      } else if (action === 'increase') {
        nextQuantity = viewState.quantity + 1;
      } else if (action === 'decrease') {
        nextQuantity = Math.max(0, viewState.quantity - 1);
      }

      setDesiredQuantity(this.variantId, nextQuantity, action === 'add');
      this._clickPending = false;
    }

    render() {
      if (!this.variantId || !this.addButton || !this.quantitySelector || !this.quantityValue) return;

      const viewState = getVariantViewState(this.variantId);
      const hasQuantity = viewState.quantity > 0;
      const isSyncing = viewState.syncing || this._clickPending === true;
      const controlsLocked = isSyncing;

      this.dataset.state = hasQuantity ? 'quantity' : 'button';
      this.classList.toggle('is-pending', viewState.pending);
      this.classList.toggle('is-syncing', isSyncing);

      this.addButton.hidden = hasQuantity;
      this.quantitySelector.hidden = !hasQuantity;
      this.quantityValue.textContent = String(viewState.quantity);

      this.addButton.disabled = controlsLocked;
      this.minusButton.disabled = controlsLocked || viewState.quantity <= 0;
      this.plusButton.disabled = controlsLocked;
    }
  }

  customElements.define('bombae-card-product-v2-form', BombaeCardProductV2Form);

  /* ── External cart refresh (GoKwik shadow-DOM, side cart, etc.) ── */
  document.addEventListener('bombae:force-cart-refresh', () => {
    cartState.cartPromise = null;
    fetchCart().catch(() => {});
  });
})();
